'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { fetchAuthedImageUrl } from '../../services/ai-generator.service';
import {
  AUTO_STYLE, ICON_STYLE, detailLabel, equipmentStyleOf, styleLabel, variantCaption, variantDetails,
} from './variants';
import { formatCredits } from '../../utils/aiPricing';
import { SHOWCASE_HERO_SRC, STYLE_THUMB_PX, styleThumbSrc } from './generatorUi';

// Rendered SVG goes into an <img>: an image context never runs embedded scripts.
const svgToDataUrl = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

/**
 * Small pill next to a price ("New price"). The text comes from the backend
 * (GET /quota → pricing.badge) and is shown as-is; nothing when it is null.
 */
export function PriceBadge({ text, onDark = false }) {
  if (!text) return null;
  return <span className={`ag-price-badge${onDark ? ' on-dark' : ''}`}>{text}</span>;
}

/**
 * One variant preview. preview_url is an authenticated endpoint, so it is
 * fetched with the bearer token (same as downloads) and shown from an object
 * URL that is revoked when the tile goes away.
 */
function VariantImage({ variant, token, alt }) {
  // An inline render needs no fetch; otherwise the fetched object URL is kept
  // together with the URL it belongs to, so a changed variant never shows a
  // stale picture.
  const inline = variant.previewSvg ? svgToDataUrl(variant.previewSvg) : null;
  const [loaded, setLoaded] = useState({ key: null, src: null, failed: false });

  useEffect(() => {
    if (inline || !variant.previewUrl || !token) return undefined;
    let alive = true;
    let objectUrl = null;
    const key = variant.previewUrl;
    fetchAuthedImageUrl(key, token)
      .then((url) => {
        objectUrl = url;
        if (alive) setLoaded({ key, src: url, failed: false });
        else URL.revokeObjectURL(url);
      })
      .catch(() => { if (alive) setLoaded({ key, src: null, failed: true }); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [inline, variant.previewUrl, token]);

  const current = loaded.key === variant.previewUrl ? loaded : null;
  const src = inline || current?.src || null;
  const failed = !inline && (!variant.previewUrl || !token || !!current?.failed);

  if (failed) {
    return <span className="ag-mono ag-var-note">Preview unavailable</span>;
  }
  if (!src) {
    return <div className="spinner-border spinner-border-sm text-secondary" role="status" />;
  }
  // An object/data URL of a server render; next/image has nothing to optimise here.
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt={alt} />;
}

const formatExpiry = (unixSeconds) => {
  if (!unixSeconds) return null;
  try {
    return new Date(unixSeconds * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return null;
  }
};

/**
 * Larger preview of one variant, over the page. Closes on Escape, on the
 * backdrop and on the close button; "Use this one" picks it straight away.
 */
function VariantModal({
  variant, caption, details, token, onClose, onChoose, busy,
}) {
  const closeRef = useRef(null);
  // The latest onClose, so the key listener is bound once per opening and the
  // focus is not pulled back to the close button on every parent render.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // The backdrop click is a mouse convenience; Escape and the close button
  // cover the keyboard.
  /* eslint-disable jsx-a11y/click-events-have-key-events,
     jsx-a11y/no-static-element-interactions,
     jsx-a11y/no-noninteractive-element-interactions */
  return (
    <div className="ag-vmodal" onClick={onClose}>
      <div
        className="ag-vmodal-box"
        role="dialog"
        aria-modal="true"
        aria-label={caption}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ag-vmodal-head">
          <b>{caption}</b>
          {details && <span className="ag-var-sub">{details}</span>}
          <button
            ref={closeRef}
            type="button"
            className="ag-vmodal-x"
            onClick={onClose}
            aria-label="Close preview"
          >
            ×
          </button>
        </div>
        <div className="ag-vmodal-img">
          <VariantImage variant={variant} token={token} alt={caption} />
        </div>
        <div className="ag-vmodal-foot">
          <button type="button" className="ag-btn ag-btn-ghost" onClick={onClose}>Back</button>
          <button
            type="button"
            className="ag-btn ag-btn-primary"
            onClick={() => onChoose(variant.idx)}
            disabled={busy}
          >
            Use this one
          </button>
        </div>
      </div>
    </div>
  );
  /* eslint-enable jsx-a11y/click-events-have-key-events,
     jsx-a11y/no-static-element-interactions,
     jsx-a11y/no-noninteractive-element-interactions */
}

/**
 * The "pick one" grid for a job waiting for a choice (awaiting_pick /
 * variants_ready). Three square tiles in a row — an icon and an equipment
 * drawing both sit well; on a phone the row scrolls sideways. A click on a
 * tile opens it larger.
 */
export function VariantsGrid({
  variants, catalog, token, choosingIdx, onChoose, expiresAt, error, disabled,
}) {
  const [zoomIdx, setZoomIdx] = useState(null);
  const busy = choosingIdx !== null && choosingIdx !== undefined;
  const expiry = formatExpiry(expiresAt);
  const zoomed = variants.findIndex((v) => v.idx === zoomIdx);
  const choose = (idx) => {
    setZoomIdx(null);
    onChoose(idx);
  };
  return (
    <div className="ag-variants">
      <div className="ag-variants-head">
        <b>Pick one — already paid for.</b>
        {' '}
        {expiry ? `The others are kept until ${expiry}.` : 'It becomes your widget — then you can edit and download it.'}
      </div>
      <div className="ag-variants-grid">
        {variants.map((v, i) => {
          const caption = variantCaption(catalog, v, i);
          const details = variantDetails(catalog, v);
          const choosing = choosingIdx === v.idx;
          return (
            <div className={`ag-var${choosing ? ' on' : ''}`} key={v.idx}>
              <button
                type="button"
                className="ag-var-thumb"
                onClick={() => setZoomIdx(v.idx)}
                aria-label={`Enlarge ${caption}`}
                title="Click to enlarge"
              >
                <VariantImage variant={v} token={token} alt={caption} />
              </button>
              <div className="ag-var-foot">
                <span className="ag-var-cap" title={details || caption}>
                  {caption}
                  {details && <span className="ag-var-sub">{details}</span>}
                </span>
                <button
                  type="button"
                  className="ag-btn ag-btn-primary ag-var-btn"
                  onClick={() => choose(v.idx)}
                  disabled={busy || disabled}
                >
                  {choosing ? 'Building…' : 'Use this one'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {expiry && (
        <div className="ag-variants-foot ag-mono">
          You can come back to them from your history.
        </div>
      )}
      {error && <div className="ag-note ag-note-alarm ag-variants-err">{error}</div>}
      {zoomed >= 0 && (
        <VariantModal
          variant={variants[zoomed]}
          caption={variantCaption(catalog, variants[zoomed], zoomed)}
          details={variantDetails(catalog, variants[zoomed])}
          token={token}
          onClose={() => setZoomIdx(null)}
          onChoose={choose}
          busy={busy || disabled}
        />
      )}
    </div>
  );
}

/**
 * Example picture for a style (public/images/ai-generator/styles/<id>.webp).
 * The pictures double as a showcase of what the generator draws. A missing
 * file falls back to the style's initial, so an unknown style id (or a
 * picture that has not been uploaded yet) still gets a usable card.
 */
function StyleThumb({ id, label, size = STYLE_THUMB_PX }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="ag-scard-ph" aria-hidden="true">
        {(label || id || '?').trim().charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <Image
      src={styleThumbSrc(id)}
      alt=""
      width={size}
      height={size}
      priority
      onError={() => setFailed(true)}
    />
  );
}

const CheckMark = () => (
  <svg className="ag-scard-check" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Shorter card captions where the catalogue label would not fit a small card
// in two lines; the full name and description are in the tooltip.
const CARD_LABELS = Object.freeze({ 'hp-hmi': 'ISA-101 (HP-HMI)' });

/** One selectable style card: picture, label, one-line description on hover. */
function StyleCard({
  id, label, description, on, onPick, disabled,
}) {
  return (
    <button
      type="button"
      className={`ag-scard${on ? ' on' : ''}`}
      onClick={() => onPick(id)}
      disabled={disabled}
      aria-pressed={on}
      title={description || label}
    >
      <span className="ag-scard-img">
        {/* key: a new id gets a fresh fallback state */}
        <StyleThumb key={id} id={id} label={label} />
        {on && <CheckMark />}
      </span>
      <span className="ag-scard-label">{label}</span>
      {description && <span className="ag-scard-desc">{description}</span>}
    </button>
  );
}

/**
 * Style picker inside the composer: "Auto", the equipment styles and "Icon"
 * as a row of picture cards (scrolls sideways with snap on a phone), and the
 * detail picker right after it when the chosen style supports it (backend
 * `details`). The cards are the only choice the user makes: Icon sends style
 * 'icon' (the backend's icon path); a widget vs an equipment drawing is the
 * backend's own call.
 */
export function StylePicker({
  catalog, styleId, detailId, onStyle, onDetail, disabled,
}) {
  const iconOn = styleId === ICON_STYLE;
  const selected = catalog.styles.find((st) => st.id === styleId) || null;
  const showDetail = !!selected && catalog.supportsDetail;
  let hint = 'Auto — the AI picks a look that fits your description.';
  if (iconOn) hint = 'A clean pictogram for a button or a menu — ready in about 10 seconds.';
  else if (selected) hint = `${selected.description}${showDetail ? ' Detail applies to equipment drawings.' : ''}`;

  // On a phone the cards are a sideways swipe row with a faded right edge; the
  // fade goes once the row is scrolled to its end, so the last card reads whole.
  const cardsRef = useRef(null);
  const [atEnd, setAtEnd] = useState(false);
  const onCardsScroll = () => {
    const el = cardsRef.current;
    if (el) setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  return (
    <div className="ag-style">
      <div className="ag-style-head">
        <span className="ag-style-k">Style</span>
        <span className="ag-scards-swipe" aria-hidden="true">Swipe for more →</span>
        <span className="ag-style-hint">{hint}</span>
      </div>
      <div
        className={`ag-scards${atEnd ? ' at-end' : ''}`}
        role="group"
        aria-label="Visual style"
        ref={cardsRef}
        onScroll={onCardsScroll}
      >
        <StyleCard
          id={AUTO_STYLE}
          label="Auto"
          description="Let the AI pick a style from your description"
          on={styleId === AUTO_STYLE}
          onPick={onStyle}
          disabled={disabled}
        />
        {catalog.styles.map((st) => (
          <StyleCard
            key={st.id}
            id={st.id}
            label={CARD_LABELS[st.id] || st.label}
            description={st.description}
            on={styleId === st.id}
            onPick={onStyle}
            disabled={disabled}
          />
        ))}
        <StyleCard
          id={ICON_STYLE}
          label="Icon"
          description="A small pictogram for a button or a menu"
          on={iconOn}
          onPick={onStyle}
          disabled={disabled}
        />
      </div>
      {showDetail && (
        <div className="ag-style-row">
          <span className="ag-style-k">Detail</span>
          <div className="ag-seg" role="group" aria-label="Detail level">
            {catalog.details.map((d) => (
              <button
                key={d.id}
                type="button"
                className={detailId === d.id ? 'on' : ''}
                onClick={() => onDetail(d.id)}
                disabled={disabled}
                aria-pressed={detailId === d.id}
                title={d.description}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Restyle switcher beside a concept-pipeline widget: the same drawing in
 * another style or detail level, as a new revision. Compact chips with a
 * small picture of each style.
 */
export function RestyleBar({
  catalog, result, busy, error, onRestyle, price, note = null,
}) {
  const current = equipmentStyleOf(result?.style);
  const currentDetail = result?.detail || null;
  return (
    <div className="ag-restyle">
      <div className="ag-restyle-head">
        <span className="ag-style-k">Restyle</span>
        <span className="ag-free">
          <b>{formatCredits(price)}</b>
          {current ? ` · now ${styleLabel(catalog, current)}${currentDetail ? `, ${detailLabel(currentDetail).toLowerCase()}` : ''}` : ''}
        </span>
        {busy && <div className="spinner-border spinner-border-sm text-secondary" role="status" />}
      </div>
      {note && <div className="ag-style-hint">{note}</div>}
      <div className="ag-style-opts" role="group" aria-label="Restyle the widget">
        {catalog.styles.map((st) => (
          <button
            key={st.id}
            type="button"
            className={`ag-chip ag-chip-thumb${current === st.id ? ' on' : ''}`}
            onClick={() => onRestyle({ style: st.id })}
            disabled={busy || current === st.id}
            aria-pressed={current === st.id}
            title={st.description}
          >
            <span className="ag-chip-img"><StyleThumb key={st.id} id={st.id} label={st.label} size={28} /></span>
            {st.label}
          </button>
        ))}
      </div>
      {catalog.supportsDetail && (
        <div className="ag-seg ag-restyle-detail" role="group" aria-label="Detail level">
          {catalog.details.map((d) => (
            <button
              key={d.id}
              type="button"
              className={currentDetail === d.id ? 'on' : ''}
              onClick={() => onRestyle({ detail: d.id })}
              disabled={busy || currentDetail === d.id}
              aria-pressed={currentDetail === d.id}
              title={d.description}
            >
              {d.label}
            </button>
          ))}
        </div>
      )}
      {error && <div className="ag-rate-err">{error}</div>}
    </div>
  );
}

// One showcase tile; a picture that fails to load drops the tile.
function ShowcaseItem({ item, onPick, disabled }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <button
      type="button"
      className="ag-show"
      onClick={() => onPick(item)}
      disabled={disabled}
      title={`Use this prompt: ${item.prompt}`}
    >
      <span className="ag-show-img">
        <Image
          src={item.src}
          alt={item.title}
          width={240}
          height={240}
          loading="eager"
          onError={() => setFailed(true)}
        />
      </span>
      <span className="ag-show-cap">
        <span className="ag-show-title">{item.title}</span>
        {item.styleLabel && <span className="ag-show-style">{item.styleLabel}</span>}
      </span>
    </button>
  );
}

/**
 * "What you can make": example widgets from
 * public/images/ai-generator/showcase/showcase.json. A click fills the prompt
 * and picks the style — it never starts a paid generation. Renders nothing
 * while the manifest is missing or empty; the caller shows its own fallback.
 */
export function Showcase({ items, onPick, disabled }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="ag-showcase">
      <div className="ag-showcase-head">
        <span className="ag-style-k">What you can make</span>
        <span className="ag-style-hint">Click one to start from its prompt — nothing is charged until you press Generate.</span>
      </div>
      <div className="ag-showcase-grid">
        {items.map((it) => (
          <ShowcaseItem key={it.file} item={it} onPick={onPick} disabled={disabled} />
        ))}
      </div>
    </div>
  );
}

/**
 * The "wow" piece of the empty state: a small HMI screen built only from
 * widgets this generator drew (silo, blower, mixer, pump, pipes, value tags).
 * Lazy-loaded, sits under the composer, hidden if the picture is missing.
 */
export function ShowcaseHero() {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <figure className="ag-hero-shot">
      <Image
        src={SHOWCASE_HERO_SRC}
        alt="An HMI screen with a steam boiler, a heat exchanger, a mixing tank and a pump connected by pipes, each with live value tags"
        width={1600}
        height={900}
        sizes="(max-width: 1400px) 100vw, 1320px"
        loading="eager"
        onError={() => setFailed(true)}
      />
      <figcaption>
        Every machine on this screen uses the generator&apos;s drawing engine and styles —
        the screen is yours to build.
      </figcaption>
    </figure>
  );
}
